import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, Divider } from "@mui/material"
import { type FC, useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { SoftReceiveForm } from "./SoftReceiveForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { HOLD_AREA_1 } from "@/constants/warehouse"
import { useAppView } from "@/hooks/useAppView"
import { type SoftReceiveFormValues, softReceiveFormSchema } from "@/screens/WarehouseReceiving/schemas"

interface SoftReceiveDialogProps {
  onClose: () => void
  onSubmit: (data: SoftReceiveFormValues) => void
  isLoading?: boolean
  estimatedPalletsCount?: number | null
}

export const SoftReceiveDialog: FC<SoftReceiveDialogProps> = ({
  onClose,
  onSubmit,
  isLoading = false,
  estimatedPalletsCount,
}) => {
  const { isTabletView } = useAppView()
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const methods = useForm<SoftReceiveFormValues>({
    resolver: zodResolver(softReceiveFormSchema),
    defaultValues: {
      estimatedPalletsCount: estimatedPalletsCount ?? undefined,
      deliveredPalletsCount: undefined,
      spacesUsed: undefined,
      comment: "",
      holdArea: HOLD_AREA_1,
    },
    mode: "onChange",
  })

  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = methods

  const isSubmitDisabled = !isValid || isLoading

  const handleClose = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const handleFormSubmit = useCallback(
    (data: SoftReceiveFormValues) => {
      onSubmit(data)
    },
    [onSubmit]
  )

  if (isCancelDialogOpen) {
    return <CancelDialog onClose={handleCancelDialogClose} onConfirm={onClose} />
  }

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth={!isTabletView && "sm"}
      fullWidth={!isTabletView}
      fullScreen={isTabletView}
      data-testid='soft-receive-dialog'
    >
      <DialogHeader title='Finish Soft Receiving' onClose={handleClose} />
      <FormProvider {...methods}>
        <form className='flex flex-1 flex-col' onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent className='px-6 py-10'>
            <SoftReceiveForm />
          </DialogContent>
          <Divider />
          <DialogActions className='justify-between px-10 py-6'>
            <Button
              appearance='outlined'
              onClick={handleClose}
              disabled={isLoading}
              data-testid='cancel-button'
              className={isTabletView ? "flex-1" : ""}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              appearance='contained'
              disabled={isSubmitDisabled}
              loading={isLoading}
              data-testid='submit-button'
              className={isTabletView ? "flex-1" : ""}
            >
              Finish Soft Receiving
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
